import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DollarSign, Plus, Trash2, RefreshCw, Pencil } from 'lucide-react';

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification' },
  { value: 'employment_check', label: 'Employment Verification' },
  { value: 'education_check', label: 'Education Verification' },
  { value: 'criminal_court_check', label: 'Criminal Court Check' },
  { value: 'court_advocate_check', label: 'Criminal Advocate Check' },
  { value: 'id_check', label: 'ID Verification' },
  { value: 'world_criminal_db', label: 'World Database Check' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'credit_check', label: 'Credit/CIBIL Check' },
  { value: 'uan_check', label: 'UAN Verification' },
  { value: 'reference_check', label: 'Reference Check' },
  { value: 'personal_reference', label: 'Personal Reference' },
  { value: 'professional_reference', label: 'Professional Reference' },
];

const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam',
  'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli', 'Daman and Diu',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala',
  'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const getCheckTypeLabel = (value) => {
  const found = CHECK_TYPES.find(t => t.value === value);
  return found ? found.label : value?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
};

export const VendorPricingDialog = ({ vendor, open, onClose, onSave }) => {
  const [pricingRows, setPricingRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    check_type: '',
    state: 'default',
    city: '',
    price: '',
    tat_days: 7
  });
  // May 24, 2026 — when set, save acts as UPDATE instead of APPEND
  const [editingPricingIdx, setEditingPricingIdx] = useState(null);

  useEffect(() => {
    if (vendor && open) {
      loadPricing();
    }
  }, [vendor, open]);

  const loadPricing = () => {
    const cp = vendor?.check_pricing;
    if (!cp) {
      setPricingRows([]);
      return;
    }
    // Normalize to flat array
    if (Array.isArray(cp)) {
      setPricingRows(cp.map(item => ({
        check_type: item.check_type || 'unknown',
        state: item.state || item.location?.split('__')[0] || 'default',
        city: item.city || item.location?.split('__')[1] || '',
        price: item.base_price || item.price || 0,
        tat_days: item.tat_days || 7,
        location: item.location || item.state || 'default'
      })));
    } else if (typeof cp === 'object') {
      const rows = [];
      for (const [checkType, locations] of Object.entries(cp)) {
        if (!locations || typeof locations !== 'object') continue;
        for (const [location, config] of Object.entries(locations)) {
          rows.push({
            check_type: checkType,
            state: config?.state || location.split('__')[0] || 'default',
            city: config?.city || location.split('__')[1] || '',
            price: config?.price || config?.base_price || 0,
            tat_days: config?.tat_days || 7,
            location
          });
        }
      }
      setPricingRows(rows);
    }
  };

  const handleAddPricing = async () => {
    if (!pricingForm.check_type || !pricingForm.price) {
      toast.error('Check type and price are required');
      return;
    }

    const newRow = {
      check_type: pricingForm.check_type,
      state: pricingForm.state || 'default',
      city: pricingForm.city || '',
      price: parseFloat(pricingForm.price),
      tat_days: parseInt(pricingForm.tat_days) || 7,
      location: pricingForm.city 
        ? `${pricingForm.state}__${pricingForm.city}` 
        : pricingForm.state
    };

    // Duplicate guard — bail when the same (check_type, location) row
    // already exists AND we're not editing it. Prevents
    // accidental double-pricing of mirrored Permanent address rows.
    const dupeIdx = pricingRows.findIndex(r =>
      r.check_type === newRow.check_type &&
      (r.location || (r.city ? `${r.state}__${r.city}` : r.state)) === newRow.location
    );
    if (dupeIdx !== -1 && editingPricingIdx !== dupeIdx) {
      toast.error('Duplicate: pricing for this (check type + state + city/pincode) already exists. Use the Edit button on that row instead.');
      return;
    }

    let updatedRows;
    if (editingPricingIdx !== null) {
      updatedRows = pricingRows.map((r, i) => i === editingPricingIdx ? newRow : r);
    } else {
      updatedRows = [...pricingRows, newRow];
    }

    try {
      await savePricingToBackend(updatedRows);
      setPricingRows(updatedRows);
      setPricingForm({ check_type: '', state: 'default', city: '', price: '', tat_days: 7 });
      setEditingPricingIdx(null);
      setShowAddDialog(false);
      toast.success(editingPricingIdx !== null ? 'Pricing updated' : 'Pricing added successfully');
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    }
  };

  const handleDeletePricing = async (index) => {
    const updatedRows = pricingRows.filter((_, i) => i !== index);

    try {
      await savePricingToBackend(updatedRows);
      setPricingRows(updatedRows);
      toast.success('Pricing removed');
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove pricing');
    }
  };

  const savePricingToBackend = async (rows) => {
    const checkPricingArray = rows.map(row => ({
      check_type: row.check_type,
      base_price: row.price,
      state: row.state || 'default',
      city: row.city || '',
      location: row.location || row.state || 'default',
      tat_days: row.tat_days || 7
    }));

    await axios.post(`/subvendors/${vendor.id}/pricing`, {
      check_pricing: checkPricingArray
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="ops-pricing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Pricing Configuration - {vendor?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Configure pricing for each check type by state and city/location. Default pricing applies unless overridden.
          </p>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Check Type</TableHead>
                <TableHead>State</TableHead>
                <TableHead>City/Location / Pincode Prefix</TableHead>
                <TableHead>Price (&#8377;)</TableHead>
                <TableHead>TAT</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : pricingRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No pricing configured for this vendor
                  </TableCell>
                </TableRow>
              ) : (
                pricingRows.map((row, idx) => (
                  <TableRow key={`${row.check_type}-${row.location}-${idx}`}>
                    <TableCell>{getCheckTypeLabel(row.check_type)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {row.state === 'default' || !row.state ? 'All States' : row.state}
                      </Badge>
                    </TableCell>
                    <TableCell>{row.city || '-'}</TableCell>
                    <TableCell>&#8377;{row.price}</TableCell>
                    <TableCell>{row.tat_days} days</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Edit this pricing row"
                          onClick={() => {
                            setEditingPricingIdx(idx);
                            setPricingForm({
                              check_type: row.check_type,
                              state: row.state || 'default',
                              city: row.city || '',
                              price: String(row.price ?? ''),
                              tat_days: row.tat_days || 7,
                            });
                            setShowAddDialog(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => handleDeletePricing(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex justify-center">
            <Button variant="outline" onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add New Pricing
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Add / Edit Pricing Sub-Dialog */}
        <Dialog open={showAddDialog} onOpenChange={(o) => { setShowAddDialog(o); if (!o) setEditingPricingIdx(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                {editingPricingIdx !== null ? 'Edit Pricing' : 'Add Pricing'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Check Type *</Label>
                <Select
                  value={pricingForm.check_type}
                  onValueChange={(v) => setPricingForm(prev => ({ ...prev, check_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select check type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHECK_TYPES.map(check => (
                      <SelectItem key={check.value} value={check.value}>{check.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State</Label>
                  <Select
                    value={pricingForm.state}
                    onValueChange={(v) => setPricingForm(prev => ({ ...prev, state: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                      <SelectItem value="default">All States</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City/Location / Pincode Prefix</Label>
                  <Input
                    value={pricingForm.city}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder='e.g. "Chennai" or "6000" (matches pincodes starting with 6000)'
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter a city name OR a numeric pincode prefix — prefix matches any address pincode starting with those digits.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (&#8377;) *</Label>
                  <Input
                    type="number"
                    value={pricingForm.price}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="500"
                  />
                </div>
                <div>
                  <Label>TAT (Days)</Label>
                  <Input
                    type="number"
                    value={pricingForm.tat_days}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, tat_days: parseInt(e.target.value) || 7 }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingPricingIdx(null); }}>Cancel</Button>
              <Button onClick={handleAddPricing} className="bg-emerald-600 hover:bg-emerald-700">
                {editingPricingIdx !== null ? 'Save Changes' : 'Add Pricing'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default VendorPricingDialog;
